import { Button, TextField } from '@mui/material';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import { MdList, MdRoundGridView } from '@teamworksdev/icons';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewer } from '../../context/viewer';
import { DisabledWithTooltip } from '../permissions/DisabledWithTooltip';
import { DateRange } from '../dates/RangeCalendar';
import { TeamSelect } from '../layout/TeamSelect';
import { TabPanel, Tabs, TabTrigger } from '../tab/tab';
import { DataTableWithContext } from '../table/data-table';
import {
  DataTableFilter,
  FilterDataType,
  FilterOption,
} from '../table/data-table-context';
import { DataTableFilters } from '../table/dataTableFilters';
import { cn } from '../utils';
import { GridView } from './gridView';
import { GalleryViewMode, PaginatedItems } from './types';

type GalleryCreateGating =
  | { createDisabled?: false; createDisabledReason?: string }
  | { createDisabled: true; createDisabledReason: string };

type GalleryProps<T> = {
  data: PaginatedItems<T> | null;
  viewMode: GalleryViewMode;
  isLoading: boolean;
  page: number;
  pageSize: number;
  setViewMode: (mode: GalleryViewMode) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  title: string;
  createButtonText: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  filters?: DataTableFilter[];
  pageSizeOptions?: number[];
  onSearchChange?: (searchTerm: string) => void;
  onFilterChange?: (filters: Record<string, FilterDataType[]>) => void;
  onResetFilters?: () => void;
  onRowClick?: (item: T) => void;
  searchValue?: string;
  selectedTeam?: string;
  setSelectedTeam?: (team: string) => void;
  showBaseLibrary?: boolean;
  disableGridView?: boolean;
  /** Revoked Create permission: render the CTA disabled with its reason. */
  createDisabled?: boolean;
  createDisabledReason?: string;
  renderCard?: (
    item: T,
    viewMode: GalleryViewMode,
    setSelected: (item: T) => void,
  ) => ReactNode;
  renderEditSheet?: (
    selected: T,
    setSelected: (item: T | null) => void,
  ) => ReactNode;
  renderCreateSheet?: (
    isOpen: boolean,
    setIsOpen: (isOpen: boolean) => void,
  ) => ReactNode;
  headerActions?: ReactNode;
} & GalleryCreateGating;

const initializeFilters = (
  filters: DataTableFilter[] = [],
  defaultValues: Record<string, (string | number)[] | DateRange> = {},
) => {
  return Object.entries(defaultValues).reduce<
    Record<string, FilterOption[] | FilterOption>
  >((values, [filterId, filterValues]) => {
    const filter = filters?.find((f) => f.id === filterId);
    let value: FilterOption[] | FilterOption = [];
    if (filter?.type === 'select') {
      if (Array.isArray(filterValues)) {
        value =
          filter?.['options']?.filter((o: FilterOption) =>
            filterValues.includes(o.id),
          ) ?? [];
      } else {
        value = [];
      }
    } else if (filter?.type === 'dateRange') {
      value = {
        id: 'dateRange',
        value: filterValues,
      };
    }

    return {
      ...values,
      [filterId]: value,
    };
  }, {});
};

export function Gallery<T>({
  data,
  viewMode,
  isLoading,
  page,
  pageSize,
  setViewMode,
  setPage,
  setPageSize,
  title,
  createButtonText,
  columns,
  filters,
  pageSizeOptions = [10, 25, 50, 100],
  onSearchChange,
  onFilterChange,
  onResetFilters,
  onRowClick,
  searchValue,
  disableGridView,
  renderCard,
  renderEditSheet,
  renderCreateSheet,
  headerActions,
  selectedTeam,
  setSelectedTeam,
  showBaseLibrary = true,
  createDisabled = false,
  createDisabledReason,
}: GalleryProps<T>) {
  // Force list view when grid is disabled
  const effectiveViewMode = disableGridView ? 'list' : viewMode;
  const { t } = useTranslation();
  const {
    org: {
      settings: { enableTeamLibrary },
    },
  } = useViewer();
  const [selected, setSelected] = useState<T | null>(null);
  const [createSheetOpen, setCreateSheetOpen] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<
    Record<string, FilterOption[] | FilterOption>
  >(() => initializeFilters(filters, {}));

  const handleRowClick = useCallback(
    (item: T) => {
      setSelected(item);
      onRowClick?.(item);
    },
    [onRowClick],
  );

  const handlePaginationChange = useCallback(
    (pagination: PaginationState) => {
      setPage(pagination.pageIndex);
      if (pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize);
      }
    },
    [setPage, setPageSize, pageSize],
  );

  const resetFilters = useCallback(() => {
    setFilterState(() => initializeFilters(filters, {}));
  }, [filters]);

  // Determine if filters are applied
  const isFilterApplied = Object.values(filterState).some((v) =>
    Array.isArray(v)
      ? v.length > 0
      : Boolean((v as FilterOption<DateRange>)?.value),
  );

  const showFilterRow = onSearchChange || (filters && filters.length > 0);

  const handleClearSearch = useCallback(() => {
    onSearchChange?.('');
    resetFilters();
    onResetFilters?.();
  }, [onSearchChange, resetFilters, onResetFilters]);

  // Call onFilterChange when filterState changes
  useEffect(() => {
    if (!filters?.length) return;

    // Initialize with all filter IDs to ensure we send empty arrays for cleared filters
    const extractedFilters = filters.reduce(
      (result, filter) => {
        const value = filterState[filter.id];
        if (Array.isArray(value)) {
          result[filter.id] = value.map((option) => option.value);
        } else if (value && !Array.isArray(value) && value?.value) {
          result[filter.id] = [value.value];
        } else {
          result[filter.id] = [];
        }
        return result;
      },
      {} as Record<string, FilterDataType[]>,
    );

    onFilterChange?.(extractedFilters);
  }, [filterState, onFilterChange, filters]);

  return (
    <div className="flex flex-col gap-6 w-full h-full">
      <div className="flex justify-between w-full items-center">
        <div className="text-2xl font-semibold">{title}</div>
        <div className="flex flex-row items-center gap-4">
          {enableTeamLibrary && setSelectedTeam && (
            <TeamSelect
              teamId={selectedTeam}
              onSelect={setSelectedTeam}
              showBaseLibrary={showBaseLibrary}
              sx={{ margin: 0 }}
            />
          )}
          {!disableGridView && (
            <Tabs
              size="small"
              value={effectiveViewMode}
              onChange={(_, newValue) => setViewMode(newValue)}
              className="w-[88px]"
              aria-label={t('viewMode')}
            >
              <TabTrigger
                value={'list'}
                label={<MdList size={16} aria-hidden="true" />}
                aria-label={t('listView')}
              />
              <TabTrigger
                value={'grid'}
                label={
                  <MdRoundGridView size={16} fill="black" aria-hidden="true" />
                }
                aria-label={t('gridView')}
              />
            </Tabs>
          )}
          {headerActions && (
            <div className="flex-shrink-0">{headerActions}</div>
          )}
          {(!!renderCreateSheet || createDisabled) && (
            <div className="flex-shrink-0">
              <DisabledWithTooltip
                disabled={createDisabled}
                // GalleryCreateGating makes the reason required whenever
                // createDisabled is true, so the fallback is unreachable for
                // valid call sites — destructuring just can't preserve the
                // union correlation.
                reason={createDisabledReason ?? ''}
              >
                <Button onClick={() => setCreateSheetOpen(true)}>
                  {createButtonText}
                </Button>
              </DisabledWithTooltip>
            </div>
          )}
        </div>
      </div>

      <div className="flex grow overflow-hidden">
        <TabPanel
          value={effectiveViewMode}
          tab="grid"
          className="w-full flex flex-col"
        >
          {showFilterRow && (
            <div
              className={cn(
                'flex justify-between items-center gap-2 mb-6 flex-shrink-0 overflow-x-auto overflow-y-hidden',
              )}
            >
              {onSearchChange && (
                <TextField
                  sx={{ mt: 0, minWidth: '200px' }}
                  value={searchValue || ''}
                  onChange={(e) => {
                    onSearchChange(e.target.value);
                  }}
                  placeholder={t('search')}
                />
              )}
              {filters && filters.length > 0 && (
                <div className="flex flex-row gap-2">
                  <DataTableFilters<T>
                    filters={filters}
                    filterState={filterState}
                    setFilterState={setFilterState}
                    setSearchValue={() => {}}
                    onResetFilters={() => {
                      resetFilters();
                      onResetFilters?.();
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {data && renderCard && (
            <GridView<T>
              items={data}
              viewMode={effectiveViewMode}
              setSelected={setSelected}
              page={page}
              pageSize={pageSize}
              totalCount={data.totalCount}
              setPage={setPage}
              isLoading={isLoading}
              renderCard={renderCard}
              searchValue={searchValue}
              isFilterApplied={isFilterApplied}
              onClearSearch={handleClearSearch}
            />
          )}
        </TabPanel>

        <TabPanel value={effectiveViewMode} tab="list" className="w-full">
          <DataTableWithContext<T>
            title={`${data?.totalCount || 0} ${title}`}
            columns={columns}
            data={data?.records || []}
            loading={isLoading}
            enablePagination={true}
            enableSearch={!!onSearchChange}
            enableFilters={!!filters}
            searchPlaceholder={t('search')}
            searchValue={searchValue}
            filters={filters}
            controlledFilterState={filterState}
            onFilterStateChange={setFilterState}
            initialPageSize={pageSize}
            onSearchChange={onSearchChange}
            onResetFilters={() => {
              resetFilters();
              onResetFilters?.();
            }}
            onFilterChange={onFilterChange}
            pageIndex={page}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            rowCount={data?.totalCount || 0}
            onRowClick={handleRowClick}
            onPaginationChange={handlePaginationChange}
          />
        </TabPanel>
      </div>
      {selected && renderEditSheet && renderEditSheet(selected, setSelected)}
      {renderCreateSheet &&
        renderCreateSheet(createSheetOpen, setCreateSheetOpen)}
    </div>
  );
}
